import React from 'react';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { LinkedLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
    linkLogoToExternalSite,
  } = props;

  const MailingListForm = () => {
    return (
      <div className={css.mailingListWrapper}>
        <h2 className={css.mailingListTitle}>Join our mailing list</h2>
        <form
          className={css.mailingListForm}
          action="https://youngloved.us13.list-manage.com/subscribe/post?u=792f0aeb66e170f3152e6d2b8&id=052112c88f"
          method="POST"
          target="_blank"
        >
          <input
            type="text"
            name="FNAME"
            placeholder="First Name"
            className={css.mailingListInput}
            required
          />
          <input
            type="email"
            name="EMAIL"
            placeholder="Email Address"
            className={css.mailingListInput}
            required
          />
          <button type="submit" className={css.mailingListButton}>Join</button>
        </form>
      </div>
    );
  };

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {/* Wavy View - positioned above the footer content */}
      <div className={css.waveWrapper}>
        <svg
          className={css.wave}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0 C300,50 500,80 700,30 C900,-20 1100,30 1200,0 L1200,120 L0,120 Z"
            className={css.wavePath}
          ></path>
        </svg>
      </div>
      <div className={css.footer}>
        {/* Add this above the logo */}
        <MailingListForm />
        {/* Logo Section */}
        <div className={css.logo}>
          <LinkedLogo
            rootClassName={css.logoLink}
            logoClassName={css.logoWrapper}
            logoImageClassName={css.logoImage}
            linkToExternalSite={linkLogoToExternalSite}
            layout={logoLayout}
          />
        </div>
        {/* First Column Options */}
        <div className={css.columnOptions}>
          <BlockBuilder blocks={blocks.slice(0, 1)} sectionId={sectionId} options={options} />
        </div>
        {/* Copyright */}
        <div className={css.copyright}>
          <Field data={copyright} />
        </div>
        {/* Second Column Options */}
        <div className={css.secondColumnOptions}>
          <BlockBuilder blocks={blocks.slice(1)} sectionId={sectionId} options={options} />
        </div>
      </div>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
